/**
 * @fileoverview Show one phone call in a modal: a finished call with its timeline, ring attempts and recording, or a call in progress that refreshes until it ends and then hands over to the finished record. Also exports the status chip and client badge the dashboard lists reuse.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, X } from '@phosphor-icons/react';

import api from '../../api';
import { AppSpinner } from '../shared';
import {
  audioQuality, formatClock, formatDay, formatPhone, formatSeconds, partyTitle, resolveApiHref,
} from './phoneDashboardFormat';

// How a call ended, as a coloured pill. In a list it ends in "…" when it doesn't fit; the title has it all.
export function StatusChip({ status, label }) {
  return <span className={`pd-chip pd-tone--${status || 'other'}`} title={label}>{label}</span>;
}

export function ClientBadge({ client, compact = false }) {
  if (!client) return null;
  return (
    <span className="pd-client" title={client.more_clients ? `${client.more_clients + 1} clients share this number` : undefined}>
      <Briefcase size={12} weight="bold" aria-hidden="true" />
      Client
      {!compact && client.matters?.length ? (
        <>
          {' · '}
          {client.matters.map((matter, index) => (
            <React.Fragment key={matter}>
              {index ? ', ' : ''}
              <Link to={`/case_details/${encodeURIComponent(matter)}`} onClick={(e) => e.stopPropagation()}>{matter}</Link>
            </React.Fragment>
          ))}
        </>
      ) : null}
    </span>
  );
}

function elapsed(fromIso, toIso) {
  if (!fromIso || !toIso) return null;
  const seconds = Math.round((new Date(toIso) - new Date(fromIso)) / 1000);
  if (seconds <= 0) return null;
  return `+${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

// A call's details, centred over the dimmed page. Escape or a click outside closes it.
function CallModal({ label, onClose, children }) {
  const closeButton = useRef(null);
  useEffect(() => {
    const onKey = (event) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const scroll = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButton.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = scroll;
    };
  }, [onClose]);

  return (
    <div className="pd-modal-layer" role="presentation" onClick={onClose}>
      <div className="pd-modal" role="dialog" aria-modal="true" aria-label={label} onClick={(event) => event.stopPropagation()}>
        <button ref={closeButton} type="button" className="pd-modal__close" onClick={onClose} aria-label="Close">
          <X size={16} aria-hidden="true" />
        </button>
        {children}
      </div>
    </div>
  );
}

function Timeline({ steps, startedAt, now }) {
  return (
    <section className="pd-section">
      <h3>{now ? 'So far' : 'What happened'}</h3>
      <ol className="pd-timeline">
        {steps.map((step, index) => (
          <li key={index} className={`pd-timeline__step pd-timeline__step--${step.kind}`}>
            <span className="pd-timeline__dot" aria-hidden="true" />
            <span className="pd-timeline__time">
              {step.at ? formatClock(step.at, true) : ''}
              {step.at && index > 0 ? <small>{elapsed(startedAt, step.at)}</small> : null}
            </span>
            <span className="pd-timeline__text">{step.text}</span>
          </li>
        ))}
        {now ? (
          <li className="pd-timeline__step pd-timeline__step--now">
            <span className="pd-timeline__dot" aria-hidden="true" />
            <span className="pd-timeline__time">Now<small>{elapsed(startedAt, now.at)}</small></span>
            <span className="pd-timeline__text">{now.text}</span>
          </li>
        ) : null}
      </ol>
    </section>
  );
}

function PhoneCallModal({ callId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [audioFailed, setAudioFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setDetail(null);
    setError(null);
    setAudioFailed(false);
    api.get(`/admin/phone-dashboard/calls/${callId}`)
      .then(({ data }) => active && setDetail(data))
      .catch(() => active && setError('This call could not be loaded.'));
    return () => { active = false; };
  }, [callId]);

  const quality = detail ? audioQuality(detail.mos) : null;
  const numbers = detail ? [
    ['Waited', detail.queue_wait !== null ? formatSeconds(detail.queue_wait) : null],
    ['Talked', detail.talk_seconds !== null ? formatSeconds(detail.talk_seconds) : null],
    ['On hold', detail.hold_seconds ? formatSeconds(detail.hold_seconds) : null],
    ['Whole call', formatSeconds(detail.duration)],
    ['Line quality', quality ? quality.label : null],
  ].filter(([, value]) => value) : [];

  return (
    <CallModal label="Call details" onClose={onClose}>
      {error ? <p>{error}</p> : !detail ? <div className="pd-center"><AppSpinner /></div> : (
        <>
          <header className="pd-modal__header">
            <span className="pd-eyebrow">
              {detail.direction === 'outbound' ? 'Outgoing' : detail.direction === 'local' ? 'Internal' : 'Incoming'}
              {' · '}{formatDay(detail.start_time)} {formatClock(detail.start_time)}
            </span>
            <h2>{partyTitle(detail)}</h2>
            <div className="pd-modal__sub">
              {detail.party_number && partyTitle(detail) !== formatPhone(detail.party_number) && <span>{formatPhone(detail.party_number)}</span>}
              {detail.line && <span>{detail.direction === 'outbound' ? 'From' : 'Called'} {detail.line}</span>}
            </div>
            <div className="pd-modal__tags">
              <StatusChip status={detail.status} label={detail.status_label} />
              {detail.intake && <span className="pd-tag">Intake</span>}
              <ClientBadge client={detail.client} />
            </div>
          </header>

          {numbers.length > 0 && (
            <dl className="pd-numbers">
              {numbers.map(([label, value]) => (
                <div key={label} className={label === 'Line quality' && quality ? `pd-numbers--${quality.tone}` : undefined}
                  title={label === 'Line quality' ? `${detail.mos.toFixed(1)} of 5. How clean the audio reaching the phone system was. Not a recording.` : undefined}>
                  <dt>{label}</dt><dd>{value}</dd>
                </div>
              ))}
            </dl>
          )}

          {!(detail.recording_url || detail.voicemail_link) && (
            <p className="pd-muted pd-no-audio">No recording for this call.</p>
          )}
          {(detail.recording_url || detail.voicemail_link) && (
            <section className="pd-section">
              <h3>{detail.status === 'voicemail' ? 'Voicemail' : 'Recording'}</h3>
              {detail.recording_url && audioFailed ? (
                <p className="pd-muted">
                  The recording could not be played here.{' '}
                  <a href={resolveApiHref(detail.recording_url)} target="_blank" rel="noreferrer">Open it in a new tab</a>
                </p>
              ) : detail.recording_url ? (
                <audio controls preload="metadata" src={resolveApiHref(detail.recording_url)} aria-label="Call audio"
                  onError={() => setAudioFailed(true)} />
              ) : (
                <a href={detail.voicemail_link} target="_blank" rel="noreferrer">Open in the voicemail player</a>
              )}
              {detail.transcription && <blockquote className="pd-transcript">{detail.transcription}</blockquote>}
            </section>
          )}

          <Timeline steps={detail.timeline} startedAt={detail.start_time} />

          <details className="pd-section pd-raw">
            <summary>Technical details</summary>
            <dl>
              <div><dt>Caller ID as sent</dt><dd>{detail.caller_id_name || '—'}</dd></div>
              <div><dt>Phone system result</dt><dd>{detail.outcome}</dd></div>
              {detail.mos !== null && <div><dt>Line quality score</dt><dd>{detail.mos.toFixed(2)} of 5 (4 and up sounds fine)</dd></div>}
              <div><dt>Number called</dt><dd>{formatPhone(detail.number_called) || '—'}</dd></div>
              {detail.menu_key && <div><dt>Menu key</dt><dd>{detail.menu_key}</dd></div>}
              {detail.legs.filter((leg) => leg.mos !== null).map((leg, index) => (
                <div key={index}><dt>Audio on {leg.staff_name || formatPhone(leg.destination)}'s leg</dt><dd>{leg.mos.toFixed(2)}</dd></div>
              ))}
            </dl>
          </details>
        </>
      )}
    </CallModal>
  );
}

const DIRECTION_WORDS = { inbound: 'Incoming', outbound: 'Outgoing', local: 'Internal' };

// A call in progress, refreshed every pollMs. Once it ends, onEnded gets its finished call's id
// as soon as the phone server has copied it.
export function LiveCallModal({ callUuid, pollMs, onClose, onEnded }) {
  const [live, setLive] = useState(null);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());
  const busy = useRef(false);

  useEffect(() => {
    let active = true;
    setLive(null);
    setError(null);
    const load = () => {
      if (busy.current || document.hidden) return;
      busy.current = true;
      api.get(`/admin/phone-dashboard/live/${encodeURIComponent(callUuid)}`)
        .then(({ data }) => {
          if (!active) return;
          if (data.ended && data.call_id) onEnded(data.call_id);
          else setLive((current) => (data.ended ? { ...current, ended: true } : data));
          setError(null);
        })
        .catch(() => active && setError('This call could not be loaded.'))
        .finally(() => { busy.current = false; });
    };
    load();
    const timer = setInterval(load, pollMs);
    return () => { active = false; clearInterval(timer); };
  }, [callUuid, pollMs, onEnded]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const call = live && live.call_uuid ? live : null;
  const nowIso = new Date(now).toISOString();
  const numbers = call ? [
    ['Call so far', formatSeconds((now - new Date(call.started_at)) / 1000)],
    ['Talking for', call.answered_at ? formatSeconds((now - new Date(call.answered_at)) / 1000) : null],
  ].filter(([, value]) => value) : [];

  return (
    <CallModal label="Live call details" onClose={onClose}>
      {!call ? (
        error ? <p>{error}</p>
          : live?.ended ? <p>This call has ended. Its details appear here once the phone server has saved it.</p>
            : <div className="pd-center"><AppSpinner /></div>
      ) : (
        <>
          <header className="pd-modal__header">
            <span className="pd-eyebrow">
              Live · {DIRECTION_WORDS[call.direction] || 'Call'} · started {formatClock(call.started_at)}
            </span>
            <h2>{partyTitle(call)}</h2>
            <div className="pd-modal__sub">
              {call.party_number && partyTitle(call) !== formatPhone(call.party_number) && <span>{formatPhone(call.party_number)}</span>}
              {call.line && <span>Called {call.line}</span>}
            </div>
            <div className="pd-modal__tags">
              <span className={`pd-chip pd-chip--live-${call.state}`}>{live.ended ? 'Just ended' : call.state_label}</span>
              <ClientBadge client={call.client} />
            </div>
          </header>

          <dl className="pd-numbers">
            {numbers.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
          </dl>

          <Timeline steps={call.timeline} startedAt={call.started_at}
            now={{ at: nowIso, text: live.ended ? 'Call ended' : call.state_label }} />
          {error && <p className="pd-muted">The last refresh failed; trying again.</p>}
        </>
      )}
    </CallModal>
  );
}

export default PhoneCallModal;
